import { ApiClient, CurrentLogApi, Routes, Session, ShipApi, ICalculated, ICalculationResult, ICurrentInfo, ILogbookAppStatusResult, ILogbookModel, IModelCalculations, INotable, INote, INoteAttachment, IResponse, IServerInfoResponse, ISessionResponse } from "../Types"
import { compareLogs } from "../Data/LogOrder"

/**
 * Pure client-state reducers used by the live-update subscriptions. Kept free of
 * React/DOM so their merge semantics can be unit-tested in isolation.
 *
 * Both merge by `id`: `updateData` upserts a model (mutating it in place with
 * `Object.assign`), `dataWithCalculations` (re)attaches a model's calculation and
 * notes. Crucially they REPLACE a model's `calculated` value, never accumulate it
 * — a repeated calculation broadcast overwrites the previous one rather than
 * adding to it.
 */
export const AppData = {
  updateData<T extends ILogbookModel>(data: T[], object: T, resetFields?: string[]): T[] {
    if (data.findIndex((d) => d.id == object.id) >= 0) {
      return data.map((d) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
        if ((d as any)["deletedAt"] != undefined) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
          return null as any;
        }
        if (d.id == object.id) {
          if (resetFields) {
            for (const field of resetFields) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
              (d as any)[field] = (object as any)[field];
            }
          }
          return Object.assign(d, object);
        }
        return d;
      }).filter(Boolean);
    }
    // Return a NEW array rather than mutating `data` in place: every call site does
    // `setState(updateData(state, obj))`, and React's Object.is bail-out would
    // otherwise see the same reference and silently skip the re-render -- the new
    // entity would then only appear after a reload (#1137's blank-day-note report).
    return [...data, object];
  },
  /**
   * Upsert/remove a provisional (in-progress, uncommitted) model in a map keyed by a
   * stable `key` (its `metadata.provisionalKey`). Provisional models all share id 0, so
   * they can't merge by id like `updateData` — the key identifies which entry a live
   * `update` replaces or a `delete` removes. Returns a new map (pure).
   */
  updateProvisional<T>(map: Record<string, T>, key: string | undefined, object: T, info: string): Record<string, T> {
    if (!key) {
      return map;
    }
    const next = { ...map };
    if (info == "delete") {
      delete next[key];
    } else {
      next[key] = object;
    }
    return next;
  },
  /**
   * Fold provisional models into a committed list, always sorted by `timeUtc`.
   * Committed auto-logs (e.g. a course change) are back-dated to the moment the
   * maneuver began, but arrive on the live channel after later entries and are
   * appended to the end of the list — so the merged timeline must be re-sorted by
   * time, not insertion/creation order, whether or not provisional entries exist.
   */
  mergeProvisional<T extends { id?: number; timeUtc: number }>(data: T[], provisional: T[]): T[] {
    const merged = provisional.length < 1 ? data : [...data, ...provisional];
    return merged.toSorted(compareLogs);
  },
  dataWithCalculations<T extends ILogbookModel & ICalculated<C> & INotable, C extends IModelCalculations>(data: T[], calculations?: ICalculationResult<C>[], notes?: INote[], attachments?: INoteAttachment[], id?: (note: INote) => number | undefined): T[] {
    if (calculations) {
      data = data.map((o) => {
        const calculated = calculations.find((e) => e.id == o.id);
        o.calculated = calculated?.values;
        o.calculationVersion = calculated?.version;
        return o;
      });
    }
    if (notes && id) {
      data = data.map((o) => {
        let dataNotes = notes.filter((n) => {
          const i = id(n);
          return i && i == o.id;
        });
        if (attachments) {
          dataNotes = dataNotes.map((n) => {
            n.attachments = attachments.filter((a) => a.noteId == n.id);
            return n;
          });
        }
        o.notes = dataNotes;
        return o;
      });
    }
    return data;
  },
  /**
   * Merge a live stored-calculation broadcast's parsed payload onto a calculated
   * model, pairing `.calculated` with `.calculationVersion` the same way
   * `dataWithCalculations` pairs them on initial load. Mutates `target` in place
   * (mirroring `updateData`'s merge style) and returns it so callers can chain into
   * `updateData`/`setState` at the call site. Without pairing the version too, a
   * newer-shaped broadcast payload would be merged under the model's OLD version, so
   * a version-aware reader (e.g. `StatsView`'s placeholder guard) can't detect the
   * mismatch and renders the new payload as if it still had the old shape (#1122).
   */
  applyStoredCalculation<T extends ICalculated<C>, C extends IModelCalculations>(target: T, parsed: { dataParsed: C; version: number }): T {
    target.calculated = parsed.dataParsed;
    target.calculationVersion = parsed.version;
    return target;
  },
  deleteSession(api: ApiClient): Promise<ISessionResponse> {
    return new Session(api).session.delete({ params: {} }).call();
  },
  selectShip(api: ApiClient, shipId: number, close: boolean, force: boolean = false): Promise<IResponse> {
    const body = force ? { shipId: shipId, close: close, force: true } : { shipId: shipId, close: close };
    return new ShipApi(api).ships.selectApp({ params: {}, body: body }).call();
  },
  loadStatus(api: ApiClient): Promise<ILogbookAppStatusResult> {
    return new Routes(api).status.get({ params: {} }).call();
  },
  loadCurrent(api: ApiClient): Promise<ICurrentInfo> {
    return new CurrentLogApi(api).current.get({ params: {} }).call();
  },
  loadServer(api: ApiClient): Promise<IServerInfoResponse> {
    return new Routes(api).server.get({ params: {} }).call();
  },
  loadSession(api: ApiClient): Promise<ISessionResponse> {
    return new Session(api).session.get({ params: {} }).call();
  },
  // Ask the backend to restore the device's primary session from server-side
  // ClientData after a localStorage wipe. The backend hard-gates this to the
  // loopback peer, so a companion can never obtain the primary token.
  restoreSession(api: ApiClient): Promise<ISessionResponse> {
    return new Session(api).session.restore({ params: {} }).call();
  },
};
